import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

interface ModeToggleBarProps {
  searchEnabled: boolean;
  aiEnabled: boolean;
  onSearchToggle: (enabled: boolean) => void;
  onAIToggle: (enabled: boolean) => void;
  disabled?: boolean;
  /**
   * When true, the user has 0 spendable (subscription + fias) credits. The
   * Fias AI toggle is rendered as disabled and clicking it sends them to the
   * credits store instead of toggling.
   */
  aiOutOfCredits?: boolean;
}

const baseStyles = `
  relative h-10 w-10 rounded-xl flex items-center justify-center
  transition-all duration-200 ease-out border-2
`;

const onStyles =
  'border-primary bg-background shadow-sm hover:bg-primary/10 hover:shadow-md hover:scale-105';
const offStyles =
  'border-transparent bg-muted/50 opacity-50 hover:opacity-100 hover:border-primary/40 hover:bg-muted hover:scale-105';

export function ModeToggleBar({
  searchEnabled,
  aiEnabled,
  onSearchToggle,
  onAIToggle,
  disabled = false,
  aiOutOfCredits = false,
}: ModeToggleBarProps) {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSearchToggle = () => {
    if (searchEnabled && !aiEnabled) return;
    const next = !searchEnabled;
    onSearchToggle(next);
    toast({ title: next ? 'Arche Search enabled' : 'Arche Search disabled' });
  };

  const handleAIToggle = () => {
    if (aiOutOfCredits) {
      toast({
        title: 'Out of credits',
        description: 'Visit the credits store to top up.',
      });
      navigate('/credits');
      return;
    }
    if (aiEnabled && !searchEnabled) return;
    const next = !aiEnabled;
    onAIToggle(next);
    toast({ title: next ? 'Fias AI enabled' : 'Fias AI disabled' });
  };

  // Render the AI toggle as visually disabled when out of credits, even
  // though the click handler is still wired (so it can navigate to /credits).
  const aiVisuallyDisabled = disabled || aiOutOfCredits;
  const aiActive = aiEnabled && !aiOutOfCredits;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleSearchToggle}
        disabled={disabled}
        title={searchEnabled ? 'Disable search' : 'Enable search'}
        className={`${baseStyles} ${searchEnabled ? onStyles : offStyles} ${
          disabled ? 'cursor-not-allowed !opacity-40' : 'cursor-pointer'
        }`}
      >
        <Search className={`h-6 w-6 ${searchEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
      </button>

      <button
        onClick={handleAIToggle}
        disabled={disabled}
        title={
          aiOutOfCredits
            ? 'Out of credits — click to visit the store'
            : aiEnabled
              ? 'Disable Fias AI'
              : 'Enable Fias AI'
        }
        className={`${baseStyles} ${aiActive ? onStyles : offStyles} ${
          aiVisuallyDisabled ? 'cursor-pointer !opacity-40' : 'cursor-pointer'
        }`}
      >
        <img src="/Heptagen_Logo_1.png" alt="Fias AI" className="h-7 w-7 object-contain" />
      </button>
    </div>
  );
}
